/**
 * Google Ads call-from-website conversion ("Website Calls - 60 Seconds").
 *
 * Google's documented approach (support.google.com/google-ads/answer/6095883):
 * configure the call destination with the DISPLAYED business number and a
 * `phone_conversion_callback`; Google then hands back a forwarding number and
 * WE are responsible for putting it on the page. Google measures the actual
 * forwarded call — including the 60-second minimum — so this module never
 * sends a conversion event of its own from a click.
 *
 * Boundaries (identical to every other Ads surface, unchanged by this module):
 *   - advertising consent must currently be granted, with no GPC and no
 *     stored "Do Not Sell or Share" opt-out;
 *   - the visitor must be on a public, vendor-trackable route;
 *   - the shared Google base tag is reused — no second loader, no second
 *     `js` command.
 *
 * What it rewrites: anchors whose `tel:` target is the business number. Their
 * visible number text and any number inside their `aria-label` are swapped for
 * the forwarding number; icons, labels and every other child stay exactly as
 * they were. `sms:` links, form values, JSON-LD, other phone numbers and
 * private routes are never touched. On revocation every rewritten node is
 * restored to its original text and href.
 */

import { PHONE_DISPLAY, PHONE_E164 } from "./business";
import { getCallConversionSendTo } from "./ads-config";
import { configureCallConversion, isAdvertisingAllowedNow } from "./ads-tracking";
import { subscribeConsent } from "./consent";

/** Digits of the real business line. The only number this module may touch. */
const BUSINESS_DIGITS = PHONE_E164.replace(/\D/g, "").slice(-10);

/** Any human formatting of the business number, inside a longer string. */
const BUSINESS_TEXT_RE = new RegExp(
  // Optional country code, then the number in any common human formatting.
  // Deliberately does NOT consume surrounding whitespace, so labels such as
  // "Call (310) 850-7223" keep their spacing after the swap.
  String.raw`(?:\+?1[\s.\-]*)?\(?` +
    BUSINESS_DIGITS.slice(0, 3) +
    String.raw`\)?[\s.\-]*` +
    BUSINESS_DIGITS.slice(3, 6) +
    String.raw`[\s.\-]*` +
    BUSINESS_DIGITS.slice(6),
  "g",
);

interface Original {
  href: string;
  ariaLabel: string | null;
  text: Map<Text, string>;
}

const rewritten = new Map<HTMLAnchorElement, Original>();

let initialized = false;
let observer: MutationObserver | null = null;
let forwardingDisplay: string | null = null;
let forwardingHref: string | null = null;
let configured = false;

/** Test-only reset. */
export function __resetCallTrackingForTests(): void {
  restoreAll();
  observer?.disconnect();
  observer = null;
  initialized = false;
  configured = false;
  forwardingDisplay = null;
  forwardingHref = null;
}

/** True when this anchor points at the business line. */
function isBusinessTel(a: HTMLAnchorElement): boolean {
  const raw = a.getAttribute("href") || "";
  if (!raw.toLowerCase().startsWith("tel:")) return false;
  const digits = raw.slice(4).replace(/\D/g, "");
  return digits.length >= 10 && digits.slice(-10) === BUSINESS_DIGITS;
}

function textNodesIn(el: Element): Text[] {
  const out: Text[] = [];
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let n = walker.nextNode();
  while (n) {
    out.push(n as Text);
    n = walker.nextNode();
  }
  return out;
}

function applyTo(a: HTMLAnchorElement): void {
  if (!forwardingDisplay || !forwardingHref) return;
  if (rewritten.has(a)) return;
  if (!isBusinessTel(a)) return;

  const original: Original = {
    href: a.getAttribute("href") || "",
    ariaLabel: a.getAttribute("aria-label"),
    text: new Map(),
  };

  for (const node of textNodesIn(a)) {
    const value = node.nodeValue ?? "";
    if (!BUSINESS_TEXT_RE.test(value)) {
      BUSINESS_TEXT_RE.lastIndex = 0;
      continue;
    }
    BUSINESS_TEXT_RE.lastIndex = 0;
    original.text.set(node, value);
    node.nodeValue = value.replace(BUSINESS_TEXT_RE, forwardingDisplay);
  }

  const label = original.ariaLabel;
  if (label && BUSINESS_TEXT_RE.test(label)) {
    BUSINESS_TEXT_RE.lastIndex = 0;
    a.setAttribute("aria-label", label.replace(BUSINESS_TEXT_RE, forwardingDisplay));
  }
  BUSINESS_TEXT_RE.lastIndex = 0;

  a.setAttribute("href", forwardingHref);
  rewritten.set(a, original);
}

/** Rewrite every matching link currently in the document. */
function applyAll(): void {
  if (typeof document === "undefined") return;
  if (!forwardingDisplay) return;
  document.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"], a[href^="TEL:"]').forEach(applyTo);
}

function restoreAll(): void {
  for (const [a, original] of rewritten) {
    if (!a.isConnected) continue;
    a.setAttribute("href", original.href);
    if (original.ariaLabel === null) a.removeAttribute("aria-label");
    else a.setAttribute("aria-label", original.ariaLabel);
    for (const [node, value] of original.text) {
      if (node.isConnected) node.nodeValue = value;
    }
  }
  rewritten.clear();
}

function startObserver(): void {
  if (observer || typeof MutationObserver === "undefined") return;
  observer = new MutationObserver((records) => {
    if (!forwardingDisplay) return;
    // A late grant/revocation between callback and mutation must win.
    if (!isAdvertisingAllowedNow()) {
      restoreAll();
      return;
    }
    for (const r of records) {
      r.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return;
        if (n instanceof HTMLAnchorElement) applyTo(n);
        n.querySelectorAll?.<HTMLAnchorElement>('a[href^="tel:"]').forEach(applyTo);
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

/**
 * Called by Google with the forwarding number. Guarded: a callback that
 * arrives after consent was withdrawn, after a GPC signal, or once the visitor
 * has moved to a private route changes nothing.
 */
function onForwardingNumber(formattedNumber: string): void {
  if (!formattedNumber || typeof formattedNumber !== "string") return;
  if (!isAdvertisingAllowedNow()) return;
  const digits = formattedNumber.replace(/\D/g, "");
  if (digits.length < 10) return;
  forwardingDisplay = formattedNumber;
  forwardingHref = `tel:+${digits.length === 10 ? `1${digits}` : digits}`;
  applyAll();
  startObserver();
}

/** Configure the call destination once, under current advertising permission. */
function ensureConfigured(): boolean {
  if (configured) return true;
  const sendTo = getCallConversionSendTo();
  if (!sendTo) return false;
  const ok = configureCallConversion(sendTo, {
    phone_conversion_number: PHONE_DISPLAY,
    phone_conversion_callback: onForwardingNumber,
  });
  if (ok) configured = true;
  return ok;
}

/**
 * Initialize call tracking for the session. Safe to call on every app start;
 * a later consent grant configures it, a revocation restores the real number.
 */
export function initCallTracking(): void {
  if (typeof window === "undefined") return;
  if (initialized) return;
  initialized = true;

  ensureConfigured();

  subscribeConsent(() => {
    if (isAdvertisingAllowedNow()) {
      ensureConfigured();
      // Re-apply for links rendered while the number was already known.
      if (forwardingDisplay) {
        applyAll();
        startObserver();
      }
      return;
    }
    // Withdrawal, GPC, or device opt-out: the real business number comes back
    // immediately and nothing further is rewritten.
    restoreAll();
  });
}

/** Re-check on SPA route changes: new page, newly rendered call links. */
export function refreshCallTracking(): void {
  if (typeof window === "undefined") return;
  if (!isAdvertisingAllowedNow()) {
    restoreAll();
    return;
  }
  ensureConfigured();
  if (forwardingDisplay) {
    applyAll();
    startObserver();
  }
}
